import React, { useRef, useState } from "react";
import axios from "axios";
import * as tus from "tus-js-client";
import { api } from "@/lib/api";
import { compressImageFile } from "@/lib/imageCompress";
import { videoPosterFor } from "@/lib/neutralPoster";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, Image as ImageIcon, Video as VideoIcon, X } from "lucide-react";
import { toast } from "sonner";

/** Read width/height/duration from a local video File (for Reel detection). */
const readVideoMeta = (file) =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.onloadedmetadata = () => {
        // Durée finie uniquement (Infinity/NaN = en-tête muet → le serveur sonde le fichier).
        const d = v.duration;
        const meta = { width: v.videoWidth, height: v.videoHeight, duration: isFinite(d) && d > 0 ? d : null };
        URL.revokeObjectURL(url);
        resolve(meta);
      };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      v.src = url;
    } catch {
      resolve(null);
    }
  });

/** Capture the first visible frame of a local video File as a JPEG Blob (poster). */
// Capture the first meaningful (non-black) frame of a video (from any src URL) as a
// JPEG blob. Hardened for Android WebView (Capacitor): forces a muted decode, tries
// several timestamps, lets the frame paint before reading, and rejects near-black frames.
const capturePosterBlobFromSrc = (srcUrl, { revoke = false, at = 0 } = {}) =>
  new Promise((resolve) => {
    if (!srcUrl) { resolve(null); return; }
    const v = document.createElement("video");
    v.preload = "auto";
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    let done = false;
    const cleanup = () => {
      try { v.pause(); } catch { /* noop */ }
      if (revoke) { try { URL.revokeObjectURL(srcUrl); } catch { /* noop */ } }
      try { v.removeAttribute("src"); v.load(); } catch { /* noop */ }
    };
    const finish = (blob) => { if (done) return; done = true; cleanup(); resolve(blob || null); };

    // Draw the current frame → returns a canvas, "black" (near-black frame) or null.
    const drawFrame = () => {
      const w = v.videoWidth, h = v.videoHeight;
      if (!w || !h) return null;
      const scale = Math.min(1, 720 / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext("2d");
      try { ctx.drawImage(v, 0, 0, cw, ch); } catch { return null; }
      try {
        const { data } = ctx.getImageData(0, 0, cw, ch);
        let sum = 0, n = 0;
        for (let i = 0; i < data.length; i += 4 * 137) { sum += data[i] + data[i + 1] + data[i + 2]; n += 3; }
        if (n && sum / n < 8) return "black";
      } catch { /* tainted/unsupported → accept the frame */ }
      return canvas;
    };

    const stops = [0.1, 0.4, 1, 2].map((s) => s + Math.max(0, at || 0));
    let idx = 0;
    const tryNext = () => {
      if (done) return;
      const c = drawFrame();
      if (c && c !== "black") { c.toBlob((b) => finish(b), "image/jpeg", 0.82); return; }
      if (idx >= stops.length) {
        // last resort: accept whatever we have, even if it was black
        const last = drawFrame();
        if (last && last !== "black") last.toBlob((b) => finish(b), "image/jpeg", 0.82);
        else finish(null);
        return;
      }
      const dur = v.duration && isFinite(v.duration) ? v.duration : 1;
      const target = Math.min(stops[idx++], Math.max(0, dur - 0.05));
      v.addEventListener("seeked", () => setTimeout(tryNext, 70), { once: true });
      try { v.currentTime = target; } catch { setTimeout(tryNext, 70); }
    };

    v.onloadeddata = () => {
      // Force the WebView to decode a frame (muted autoplay is allowed on Android).
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => { try { v.pause(); } catch { /* noop */ } tryNext(); }).catch(() => tryNext());
      } else {
        tryNext();
      }
    };
    v.onerror = () => finish(null);
    v.src = srcUrl;
    try { v.load(); } catch { /* noop */ }
    setTimeout(() => finish(null), 12000);
  });

export const captureVideoPoster = (file, at = 0) => {
  let url;
  try { url = URL.createObjectURL(file); } catch { return Promise.resolve(null); }
  return capturePosterBlobFromSrc(url, { revoke: true, at });
};

// Same, but from an already-resolved URL (e.g. a native Capacitor.convertFileSrc path)
// — used to make a thumbnail for a video the WebView can read but that we have no File for.
export const captureVideoPosterFromUrl = (url) => capturePosterBlobFromSrc(url, { revoke: false });

export const uploadPosterBlob = async (blob) => {
  if (!blob) return null;
  try {
    const fd = new FormData();
    fd.append("file", blob, "poster.jpg");
    fd.append("context", "poster");
    const { data } = await api.post("/upload", fd);
    return data?.url || null;
  } catch { return null; }
};

/** Generate a poster from a video File and upload it — returns its URL (or null). `at` = offset (s) of the first frame to try. */
export const generateVideoPosterUrl = async (file, at = 0) => uploadPosterBlob(await captureVideoPoster(file, at));

/** Generate a poster from a video URL (native file) and upload it — returns its URL. */
export const generateVideoPosterUrlFromUrl = async (url) => uploadPosterBlob(await captureVideoPosterFromUrl(url));


const SINGLE_PUT_MAX = 8 * 1024 * 1024;   // below this, one presigned PUT is enough
// SEQUENTIAL upload (like YouTube): one TCP stream saturates most uplinks anyway,
// and with a single stream the inter-part pauses are REAL silence windows — with
// 2+ parallel streams the pauses overlapped and the line never breathed, so the
// app's API calls and route chunks stayed stuck behind the upload (frozen pages).
const MP_CONCURRENCY = 1;
const MP_PAUSE_MIN = 350;                  // ms — adaptive breathing window between parts
const MP_PAUSE_MAX = 1500;                 // (~15% of each part's send time, clamped)
const MP_PART_RETRIES = 5;                 // per-part retries on failure (progressive backoff)
const SERVER_FALLBACK_MAX = 50 * 1024 * 1024; // above this, NEVER route the file through the backend server
const STREAM_CHUNK = 50 * 1024 * 1024;     // Cloudflare TUS chunk (>=5 MiB, /256 KiB)

/** --- Resumable Cloudflare Stream (TUS) upload, persisted across page reloads --- */
const TUS_STORE_KEY = "yalola:tus:v1";
const TUS_TTL_MS = 2 * 60 * 60 * 1000; // stored upload URLs stay usable ~ a couple hours

const _tusFp = (file) => `${file.name}|${file.size}|${file.lastModified}`;
const _tusRead = () => { try { return JSON.parse(localStorage.getItem(TUS_STORE_KEY) || "{}"); } catch { return {}; } };
const _tusWrite = (s) => { try { localStorage.setItem(TUS_STORE_KEY, JSON.stringify(s)); } catch { /* quota */ } };
const _tusPrune = (s) => {
  const now = Date.now(); let changed = false;
  for (const k of Object.keys(s)) { if (now - (s[k].savedAt || 0) > TUS_TTL_MS) { delete s[k]; changed = true; } }
  if (changed) _tusWrite(s);
  return s;
};
const _tusSave = (fp, e) => { const s = _tusRead(); s[fp] = { ...e, savedAt: Date.now() }; _tusWrite(s); };
const _tusClear = (fp) => { const s = _tusRead(); if (s[fp]) { delete s[fp]; _tusWrite(s); } };

// Video uploads interrupted mid-way and still resumable (used by the composer banner).
export const getResumableUploads = () =>
  Object.values(_tusPrune(_tusRead())).map((e) => ({ name: e.name, size: e.size, fp: e.fp }));
export const clearResumableUpload = (fp) => _tusClear(fp);

const _runTus = (file, uploadUrl, onProgress, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(new DOMException("Aborted", "AbortError")); return; }
  const up = new tus.Upload(file, {
    uploadUrl,                          // Cloudflare pre-created TUS resource
    chunkSize: STREAM_CHUNK,
    // Generous backoff: an interrupted upload auto-resumes from the last byte
    // offset as soon as the connection returns (tus resumes the server offset).
    retryDelays: [0, 2000, 4000, 8000, 15000, 30000, 30000, 30000, 60000, 60000],
    onShouldRetry: (err) => {
      const status = err?.originalResponse?.getStatus?.() || 0;
      if (status >= 400 && status < 500 && ![409, 423, 429].includes(status)) return false;
      return true; // network drops / 5xx -> keep retrying
    },
    metadata: { filename: file.name, filetype: file.type || "video/mp4" },
    onProgress: (loaded, total) => onProgress?.(total ? Math.min(99, Math.round((loaded / total) * 100)) : 0),
    onError: reject,
    onSuccess: resolve,
  });
  // Cancel: abort(true) terminates the upload on Cloudflare too (no orphaned data).
  if (signal) {
    signal.addEventListener("abort", () => {
      try { up.abort(true); } catch { /* noop */ }
      reject(new DOMException("Aborted", "AbortError"));
    }, { once: true });
  }
  up.start();
});

/**
 * Resumable video upload straight to Cloudflare Stream via the TUS protocol.
 * The one-time upload URL is provisioned server-side and persisted locally so a
 * big upload survives a page reload — re-selecting the same file resumes it.
 */
const streamUpload = async (file, context, onProgress, signal) => {
  const meta = (await readVideoMeta(file)) || {};
  const fp = _tusFp(file);
  const store = _tusPrune(_tusRead());
  const provision = async () => {
    const { data } = await api.post("/uploads/stream/create", {
      filename: file.name, size: file.size, content_type: file.type || "video/mp4", context,
    }, { signal });
    const entry = { fp, name: file.name, size: file.size, upload_url: data.upload_url, uid: data.uid, url: data.url, thumbnail: data.thumbnail };
    _tusSave(fp, entry);
    return entry;
  };
  let entry = (store[fp] && store[fp].upload_url && store[fp].url) ? store[fp] : await provision();
  try {
    await _runTus(file, entry.upload_url, onProgress, signal);
  } catch (err) {
    if (signal?.aborted) throw err;
    const status = err?.originalResponse?.getStatus?.() || 0;
    if ([403, 404, 410].includes(status)) {   // stored resume URL expired -> provision fresh once
      _tusClear(fp);
      entry = await provision();
      await _runTus(file, entry.upload_url, onProgress, signal);
    } else {
      throw err;                                // keep the entry so it can resume later
    }
  }
  _tusClear(fp);
  onProgress?.(100);
  return { url: entry.url, media_type: "video", uid: entry.uid, thumbnail: entry.thumbnail, backend: "stream", width: meta.width, height: meta.height, duration: meta.duration };
};

/** Force-upload a video straight to Cloudflare Stream (bypasses the min-MB routing). */
export const uploadToStream = (file, context = "feed", onProgress) => streamUpload(file, context, onProgress);

/** Resumable, parallel multipart upload straight to object storage (R2/S3). */
const multipartUpload = async (file, context, onProgress, meta = null, signal = null) => {
  const { data: start } = await api.post("/uploads/multipart/start", {
    filename: file.name, size: file.size, content_type: file.type || "application/octet-stream", context,
  }, { signal });
  const { upload_id, path, backend, part_size, part_count, part_urls } = start;
  const total = file.size;
  const loaded = new Array(part_count + 1).fill(0);
  const parts = new Array(part_count);
  const report = () => onProgress?.(Math.min(99, Math.round((loaded.reduce((a, b) => a + b, 0) / total) * 100)));

  const uploadPart = async (i) => {
    const from = (i - 1) * part_size;
    const blob = file.slice(from, Math.min(from + part_size, total));
    let attempt = 0;
    for (;;) {
      try {
        const res = await axios.put(part_urls[String(i)], blob, {
          signal,
          onUploadProgress: (e) => { loaded[i] = e.loaded || 0; report(); },
        });
        let etag = res.headers?.etag || res.headers?.ETag || res.headers?.Etag;
        if (!etag) throw new Error("ETag manquant (CORS ExposeHeaders ?)");
        etag = etag.replace(/^"|"$/g, "");
        loaded[i] = blob.size; report();
        parts[i - 1] = { PartNumber: i, ETag: `"${etag}"` };
        return;
      } catch (err) {
        if (signal?.aborted) throw err;
        attempt += 1;
        if (attempt > MP_PART_RETRIES) throw err;
        await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
  };

  let next = 1;
  const worker = async () => {
    for (;;) {
      const i = next; next += 1;
      if (i > part_count) return;
      const t0 = Date.now();
      await uploadPart(i);
      if (next <= part_count) {
        const pause = Math.min(MP_PAUSE_MAX, Math.max(MP_PAUSE_MIN, (Date.now() - t0) * 0.15));
        await new Promise((r) => setTimeout(r, pause));
      }
    }
  };
  try {
    await Promise.all(Array.from({ length: Math.min(MP_CONCURRENCY, part_count) }, worker));
  } catch (err) {
    try { await api.post("/uploads/multipart/abort", { path, upload_id }); } catch { /* noop */ }
    throw err;
  }
  const { data } = await api.post("/uploads/multipart/complete", {
    path, upload_id, backend, parts, filename: file.name, content_type: file.type || "application/octet-stream",
    ...(meta ? { width: meta.width, height: meta.height, duration: meta.duration } : {}),
  }, { signal });
  onProgress?.(100);
  return data;
};

/**
 * Upload a media file.
 * - Video/audio: direct-to-storage (single presigned PUT if small, else parallel+resumable multipart).
 * - Large images (> 8 MB): direct multipart too.
 * - Small images: through the backend (keeps image moderation).
 */
export const uploadMediaFile = async (file, { onProgress, config, context = "feed", signal } = {}) => {
  // Shrink + re-encode photos before upload (faster/lighter sends everywhere).
  // Keeps PNG/WebP transparency, fixes EXIF rotation, never enlarges the file.
  if (file.type && file.type.startsWith("image")) {
    const maxDim = Number(config?.settings?.media_limits?.image_max_dim) || undefined;
    try { file = await compressImageFile(file, maxDim ? { maxDim } : {}); } catch { /* keep original */ }
  }
  const isAv = file.type.startsWith("video") || file.type.startsWith("audio");
  const isImg = file.type.startsWith("image");
  const isVideo = file.type.startsWith("video");

  // Option (c): ALL video uploads go straight to R2 (fast, parallel multipart) — the
  // same fast path stories use. Adaptive HLS is produced server-side in the background
  // (see create_post → _start_hls_transcode). The old direct Cloudflare Stream TUS upload
  // was sequential and ~10x slower, so it's intentionally no longer used here.

  const s3Direct = !!config?.storage?.s3_direct_upload;
  const useDirect = s3Direct && (isAv || (isImg && file.size > SINGLE_PUT_MAX));
  if (useDirect) {
    try {
      // Measure video dimensions locally so the server can skip the slow remote ffprobe.
      const meta = isVideo ? (await readVideoMeta(file)) : null;
      if (file.size > SINGLE_PUT_MAX) {
        try {
          return await multipartUpload(file, context, onProgress, meta, signal);
        } catch (err) {
          if (signal?.aborted) throw err;
          // One automatic fresh multipart attempt (unstable network) before giving up.
          onProgress?.(0);
          return await multipartUpload(file, context, onProgress, meta, signal);
        }
      }
      const { data: pre } = await api.post("/uploads/presign", {
        filename: file.name, size: file.size, content_type: file.type || "application/octet-stream", context,
      }, { signal });
      await axios.put(pre.upload_url, file, {
        signal,
        headers: { "Content-Type": pre.content_type },
        onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      const { data } = await api.post("/uploads/complete", {
        path: pre.path, filename: file.name, content_type: pre.content_type, backend: pre.backend,
        ...(meta ? { width: meta.width, height: meta.height, duration: meta.duration } : {}),
      }, { signal });
      return data;
    } catch (e) {
      if (signal?.aborted) throw e; // user cancelled — don't fall back
      // Big files must NEVER transit through the backend (saturates the server + proxy limits):
      // either the direct upload succeeds, or the whole upload fails cleanly.
      if (file.size > SERVER_FALLBACK_MAX) throw e;
      onProgress?.(0); // direct upload failed (CORS/permissions/network) — fall back to backend
    }
  }
  const fd = new FormData();
  fd.append("file", file);
  fd.append("context", context);
  const { data } = await api.post("/upload", fd, {
    signal,
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
  });
  return data;
};

/** Validate a local file against admin-defined media limits. Returns an error string or null. */
export const validateMediaFile = (file, mediaLimits, context = "feed", opts = {}) => {
  const t = opts.t || ((k, d) => d);
  const ml = mediaLimits || {};
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const sets = {
    image: ml.image_formats || "jpg,jpeg,png,gif,webp,heic,heif",
    video: ml.video_formats || "mp4,webm,mov,m4v",
    audio: ml.audio_formats || "mp3,wav,ogg,m4a,aac",
  };
  let kind = null;
  for (const k of Object.keys(sets)) {
    if (String(sets[k]).toLowerCase().split(",").map((x) => x.trim().replace(/^\./, "")).includes(ext)) { kind = k; break; }
  }
  if (!kind) return t("upload.format_not_allowed", "Format .{ext} non autorisé").replace("{ext}", ext);
  // Story videos are trimmed & re-encoded to a tiny clip client-side before
  // upload, so we skip the size gate on the (possibly huge) source file.
  if (kind === "video" && opts.skipVideoSize) {
    const hardMax = 2048; // absolute safety cap (MB) to avoid pathological files
    if (file.size > hardMax * 1024 * 1024) return `${t("upload.too_big_video", "Vidéo trop volumineuse")} (max ${hardMax} MB)`;
    return null;
  }
  const defaults = { image: 10, video: 100, audio: 20 };
  let maxMb = Number(ml[`${kind}_max_mb`]) || defaults[kind];
  if (kind === "video" && context === "story") {
    maxMb = Number(ml.story_video_max_mb) || maxMb;
  }
  if (file.size > maxMb * 1024 * 1024) {
    const label = kind === "image" ? t("upload.too_big_image", "Image trop volumineuse") : kind === "video" ? t("upload.too_big_video", "Vidéo trop volumineuse") : t("upload.too_big_audio", "Audio trop volumineux");
    return `${label} (max ${maxMb} MB)`;
  }
  return null;
};

// URL des médias (CDN + variantes) : logique dans lib/mediaUrl.js, ré-exportée ici (imports existants).
import { resolveMediaUrl } from "@/lib/mediaUrl";
export { setMediaCdnBase, resolveMediaUrl } from "@/lib/mediaUrl";

// Neutral placeholder shown when a media file can't be loaded (e.g. an old ad
// whose original upload is missing) — avoids the ugly broken-image icon.
export const MEDIA_PLACEHOLDER =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>" +
    "<rect width='160' height='160' fill='#F1ECE6'/>" +
    "<g fill='none' stroke='#C9BBB0' stroke-width='6' stroke-linecap='round' stroke-linejoin='round'>" +
    "<rect x='46' y='52' width='68' height='56' rx='8'/>" +
    "<circle cx='68' cy='74' r='7'/>" +
    "<path d='M52 104l22-20 16 12 12-10 14 18'/></g></svg>"
  );

/** onError handler: swap a broken media <img> for the neutral placeholder once. */
export const onMediaImgError = (e) => {
  const el = e.currentTarget;
  if (el.dataset.phApplied) return;
  el.dataset.phApplied = "1";
  el.src = MEDIA_PLACEHOLDER;
};

/**
 * Reusable file picker.
 * Props:
 *   accept   — mime filter (default "image/*")
 *   multiple — allow multi-select
 *   onUploaded(urls | url) — callback fired after successful upload(s)
 *   buttonLabel — label of the button
 *   variant — "default" | "compact" | "hero"
 *   icon    — override lucide icon component
 */
export const FileUpload = ({
  accept = "image/*",
  multiple = false,
  onUploaded,
  onFiles,
  buttonLabel,
  variant = "default",
  icon: Icon,
  testId = "file-upload",
  disabled = false,
  className = "",
  context = "feed",
  allowLargeVideo = false,
  capturePoster = false,
  children,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { config, t } = useApp();

  const acceptImages = accept.includes("image");
  const acceptVideos = accept.includes("video");
  const defaultIcon = acceptVideos && !acceptImages ? VideoIcon : ImageIcon;
  const IconCmp = Icon || defaultIcon;
  const label = buttonLabel || (acceptVideos && !acceptImages ? t("upload.video", "Vidéo") : acceptImages && acceptVideos ? t("upload.media", "Média") : t("upload.photo", "Photo"));

  const openPicker = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const valid = [];
    for (const f of Array.from(files)) {
      const err = validateMediaFile(f, config?.settings?.media_limits, context, { skipVideoSize: allowLargeVideo, t });
      if (err) toast.error(`${f.name} : ${err}`);
      else valid.push(f);
    }
    if (valid.length === 0) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (onFiles) {
      onFiles(valid);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    const results = [];
    for (const f of valid) {
      let meta = {};
      if (f.type && f.type.startsWith("video")) {
        meta = (await readVideoMeta(f)) || {};
      }
      try {
        const data = await uploadMediaFile(f, { config, context });
        const serverMeta = data.width && data.height
          ? { width: data.width, height: data.height, duration: data.duration ?? meta.duration }
          : {};
        // Optional: capture a first-frame poster (client-side) for videos so
        // callers (e.g. ads) get an instant real still instead of a black tile.
        let posterExtra = {};
        if (capturePoster && f.type && f.type.startsWith("video")) {
          try { const pu = await generateVideoPosterUrl(f); if (pu) posterExtra = { poster_url: pu }; } catch { /* server generates it */ }
        }
        results.push({ url: data.url, media_type: data.media_type, size: data.size, ...meta, ...serverMeta, ...posterExtra });
      } catch (e) {
        toast.error(`Erreur upload ${f.name}: ${e?.friendlyMessage || e?.message || "?"}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (results.length === 0) return;
    onUploaded?.(multiple ? results.map((r) => r.url) : results[0].url, results);
  };

  if (variant === "hero") {
    // Big drag-drop looking area
    return (
      <>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading || disabled}
          className="w-full rounded-2xl border-2 border-dashed p-6 flex flex-col items-center gap-2 hover:bg-[color:var(--yalola-surface-alt)] transition-colors"
          style={{ borderColor: "var(--yalola-primary)" }}
          data-testid={`${testId}-btn`}
        >
          {uploading ? <Loader2 className="animate-spin" size={28} style={{ color: "var(--yalola-primary)" }} />
                     : <IconCmp size={28} style={{ color: "var(--yalola-primary)" }} />}
          <span className="text-sm font-medium">{uploading ? "Envoi en cours..." : label}</span>
          <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("upload.click_or_drop", "Cliquez ou glissez un fichier")}</span>
        </button>
      </>
    );
  }

  if (variant === "custom") {
    return (
      <>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading || disabled}
          className={className}
          data-testid={`${testId}-btn`}
        >
          {children}
          {uploading && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/70">
              <Loader2 className="animate-spin" size={22} style={{ color: "var(--yalola-primary)" }} />
            </div>
          )}
        </button>
      </>
    );
  }

  if (variant === "compact") {
    const iconOnly = buttonLabel === "";
    return (
      <>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading || disabled}
          className={iconOnly
            ? "inline-flex items-center justify-center h-9 w-9 rounded-full shadow-md hover:opacity-90 border-2 border-white"
            : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:opacity-80 shadow-sm"}
          style={{ background: iconOnly ? "var(--yalola-primary)" : "var(--yalola-surface-alt)", color: iconOnly ? "#fff" : "var(--yalola-text)" }}
          data-testid={`${testId}-btn`}
          title={iconOnly ? "Changer la photo" : undefined}
        >
          {uploading ? <Loader2 className="animate-spin" size={iconOnly ? 16 : 14} /> : <IconCmp size={iconOnly ? 16 : 14} />}
          {!iconOnly && (uploading ? "Upload..." : label)}
        </button>
      </>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid={`${testId}-input`} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-full"
        onClick={openPicker}
        disabled={uploading || disabled}
        data-testid={`${testId}-btn`}
      >
        {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <IconCmp size={14} className="mr-1" />}
        {uploading ? "Envoi..." : label}
      </Button>
    </>
  );
};

/**
 * Preview grid + remove for uploaded images/videos.
 * Accepts a list of URLs (`/api/files/...` or absolute).
 */
export const MediaGrid = ({ items = [], onRemove, columns = 3, editable = true }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={`grid grid-cols-${columns} gap-2 mt-2`}>
      {items.map((u, i) => {
        const url = resolveMediaUrl(u);
        const isVideo = /\.(mp4|webm|mov|m4v)$/i.test(u);
        return (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }}>
            {isVideo ? (
              <video src={url} poster={videoPosterFor(url)} className="w-full h-full object-cover" muted playsInline />
            ) : (
              <img src={url} alt="" className="w-full h-full object-cover" />
            )}
            {editable && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1 hover:bg-black/80"
                data-testid={`media-remove-${i}`}
              ><X size={13} /></button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FileUpload;
